import type { BasisTheoryClient } from 'services/basisTheory';
import type { BasisTheoryCardElements } from '@/hooks/libs/useBasisTheoryElements';

interface BasisTheorySubmissionOptions {
  canSaveCard: boolean;
  cardBrand: string;
  client: BasisTheoryClient;
  currency: string;
  elements: BasisTheoryCardElements;
  holderName: string;
  monthlySelectedPlan: number;
  purchaseToken: string;
  saveCard: boolean;
  submissionAttemptId: string;
}

interface BasisTheoryPaymentData {
  purchaseToken: string;
  cardValues: {
    basisTheoryTokenIntentId: string;
    cardBrand: string;
    holderName: string;
    monthlySelectedPlan?: number;
    saveCard: boolean;
  };
  paymentInfo: {
    currency: string;
    browserInfo: ReturnType<typeof buildBrowserInfo>;
  };
}

/** Build browser data required by the configured card processor during 3DS authentication. */
function buildBrowserInfo() {
  return {
    user_agent: window.navigator.userAgent,
    accept_header: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
    language: window.navigator.language || 'en-US',
    color_depth: window.screen.colorDepth,
    screen_height: window.screen.height,
    screen_width: window.screen.width,
    time_zone_offset: new Date().getTimezoneOffset(),
    java_enabled: false,
  };
}

/** Create a short-lived intent from secure card elements and build sanitized payment data. */
async function createBasisTheoryPaymentData({
  canSaveCard,
  cardBrand,
  client,
  currency,
  elements,
  holderName,
  monthlySelectedPlan,
  purchaseToken,
  saveCard,
  submissionAttemptId,
}: BasisTheorySubmissionOptions): Promise<BasisTheoryPaymentData> {
  const tokenIntent = await client.tokenIntents.create(
    {
      type: 'card',
      data: {
        number: elements.cardNumber,
        expiration_month: elements.cardExpiration.month(),
        expiration_year: elements.cardExpiration.year(),
        cvc: elements.cardCvc,
      },
    },
    {
      correlationId: submissionAttemptId,
      idempotencyKey: submissionAttemptId,
    },
  );
  if (!tokenIntent?.id) throw new Error('token_intent_reference_missing');

  return {
    purchaseToken,
    cardValues: {
      basisTheoryTokenIntentId: tokenIntent.id,
      cardBrand,
      holderName,
      ...(monthlySelectedPlan > 1 ? { monthlySelectedPlan } : {}),
      // Only propagate the opt-in when the authenticated user has the save-card capability.
      // cardPaymentAttempt maps this value to Purchase's save_card contract.
      saveCard: canSaveCard && saveCard,
    },
    paymentInfo: { currency, browserInfo: buildBrowserInfo() },
  };
}

export { buildBrowserInfo, createBasisTheoryPaymentData };
